import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import { Card, Stack, Typography } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import { CommonCostChart } from "@/pages/query/common/charts/CommonCostChart";
import { TasksListTable } from "@/pages/query/tasks/TasksListTable";
import { useEffect, useMemo } from "react";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import {
  getTasksGraphData,
  getTasksGraphSummary,
  getTasksJobs,
  getTasksListFilterValues,
} from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { Spinner } from "@altimateai/lego";
import { FilterApplicableFor } from "@/components/tags/types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";

export const TasksRoot = () => {
  useBreadcrumb("Tasks", {
    defaultParents: [{ label: "Code", pathname: "/query" }],
    truncateTo: "/query",
  });
  const { page, pageSize, setTotalItems, setTotalPages } = useTablePagination();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { searchParams } = useFiltersBySearchParams();
  const sortAttribute = searchParams.get("sortAttribute") ?? "cost";
  const sortOrder = searchParams.get("sortOrder") ?? "desc";

  // Selected filter values live in the URL (written by the DataTable
  // "More filters" sheet); read them straight back for the query.
  const warehouses = useMemo(() => searchParams.getAll("warehouses"), [searchParams]);
  const databases = useMemo(() => searchParams.getAll("databases"), [searchParams]);
  const schemas = useMemo(() => searchParams.getAll("schemas"), [searchParams]);

  const dateParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
  };

  const getFilterParams = () => ({
    page: page + 1,
    size: pageSize,
    sort: sortAttribute,
    sortOrder,
    ...dateParams,
    ...(warehouses.length > 0 ? { warehouses } : {}),
    ...(databases.length > 0 ? { databases } : {}),
    ...(schemas.length > 0 ? { schemas } : {}),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "tasks_jobs",
      page,
      pageSize,
      startDate,
      endDate,
      sortAttribute,
      sortOrder,
      warehouses,
      databases,
      schemas,
    ],
    queryFn: () => getTasksJobs(getFilterParams()),
  });

  // Sync API response totals into the datatable pagination context.
  useEffect(() => {
    if (data) {
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.pages ?? 0);
    }
  }, [data, setTotalItems, setTotalPages]);

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["tasks_graph_data", startDate, endDate],
    queryFn: () => getTasksGraphData(dateParams),
  });

  const graphSummaryQueryKey = ["tasks_graph_summary", startDate, endDate];

  const { data: apiFilters } = useQuery({
    queryKey: ["tasks_job_list_filters", startDate, endDate],
    queryFn: () => getTasksListFilterValues(dateParams),
  });

  return (
    <Card className="al-p-4">
      <Stack direction="column">
        <Stack align={"center"} justify={"between"}>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Typography variant="page-heading">Tasks</Typography>
            <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.WORKLOADS} />
          </Stack>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
            onDateRangeSelect={onUpdate}
          />
        </Stack>
        <Stack direction="column">
          {isGraphLoading ? (
            <Stack
              align={"center"}
              justify={"center"}
              className={"al-w-full al-border al-rounded-md al-min-h-32"}
            >
              <Spinner />
              Loading graph...
            </Stack>
          ) : graphData?.data && graphData.data.length > 0 ? (
            <CommonCostChart
              graphData={graphData.data ?? []}
              graphSummaryQueryKey={graphSummaryQueryKey}
              graphSummaryQueryFn={getTasksGraphSummary}
              graphSummaryParams={dateParams}
            />
          ) : (
            <div className="al-w-full al-text-center">No data is present</div>
          )}
        </Stack>
        <Stack direction={"column"} gap={"lg"} className={"al-mt-4"}>
          <Typography variant="page-heading">Job list</Typography>
          <TasksListTable
            isLoading={isLoading}
            tableData={data}
            filters={{
              warehouses: apiFilters?.warehouses ?? [],
              databases: apiFilters?.databases ?? [],
              schemas: apiFilters?.schemas ?? [],
            }}
            applicableFor={FilterApplicableFor.TASKS}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
